import { getDb, isDatabaseReady } from '../index';
import PendingRepository from '../repositories/PendingRepository';
import CaseRepository from '../repositories/CaseRepository';
import JurorRepository from '../repositories/JurorRepository';
import WebSocketClient from './WebSocketClient';
import { API_ENDPOINT } from '../../util/constants';
import { isLocalId, calculateDiff, mergeCreatedJurorWithLocal } from './diffUtils';

/**
 * SyncEngine
 * 
 * Handles bidirectional sync between local Dexie DB and server:
 * - Processes pending operations queue (local -> server)
 * - Handles incoming WebSocket updates (server -> local)
 * - Performs catch-up sync on reconnection
 */

class SyncEngine {
  constructor() {
    this.isProcessing = false;
    this.syncInProgress = false;
    this.retryTimeout = null;
    this.pendingQueueDebounceTimer = null;
    this.eventUnsubscribers = [];
    this.initialized = false;
    this.lastSuccessfulSync = null; // Timestamp of last successful server sync
    this.consecutiveSyncFailures = 0;
    this.lastSyncError = null;
    this.deferredRemoteJurors = new Map();
    this.subscribedCaseIds = new Set();
  }

  async applyRemoteJurorRecord(jurorId, caseId, serverJuror, source = 'remote') {
    const db = getDb();
    if (!db || !jurorId) {
      return false;
    }

    const hasPending = await db.pendingOperations
      .where({ entityType: 'juror', entityId: jurorId })
      .first();
    if (hasPending) {
      this.deferredRemoteJurors.set(jurorId, { caseId, serverJuror, source });
      console.log(`Deferred remote juror update (${source}):`, jurorId);
      return false;
    }

    const existing = await db.jurors.get(jurorId);
    if (existing) {
      const incomingRevision = serverJuror.revision;
      const localRevision = Math.max(
        existing.revision || 0,
        existing._serverRevision || 0,
      );

      if (
        source !== 'sync-response' &&
        incomingRevision != null &&
        incomingRevision <= localRevision
      ) {
        return false;
      }

      await db.jurors.put({
        ...existing,
        ...serverJuror,
        revision: incomingRevision ?? existing.revision,
        _serverRevision: incomingRevision ?? existing._serverRevision,
        _synced: true,
      });
      console.log(`Applied remote juror update (${source}):`, jurorId);
      return true;
    }

    await JurorRepository.upsert({
      ...serverJuror,
      id: serverJuror._id || jurorId,
      caseId,
      _synced: true,
      _serverRevision: serverJuror.revision ?? 0,
    });
    return true;
  }

  async flushDeferredRemoteJurors() {
    if (this.deferredRemoteJurors.size === 0) {
      return;
    }

    const entries = [...this.deferredRemoteJurors.entries()];
    this.deferredRemoteJurors.clear();

    for (const [jurorId, { caseId, serverJuror, source }] of entries) {
      await this.applyRemoteJurorRecord(jurorId, caseId, serverJuror, source);
    }
  }

  /**
   * Initialize the sync engine
   * Sets up WebSocket event listeners and starts processing
   */
  async initialize() {
    if (this.initialized) return;

    // Set up WebSocket event handlers
    this.setupEventHandlers();

    // Try to connect WebSocket
    try {
      await WebSocketClient.connect();
    } catch (e) {
      console.warn('Initial WebSocket connection failed, will retry');
    }

    this.initialized = true;
    console.log('SyncEngine initialized');

    // Process any pending operations from before this session
    this.processPendingQueue();

    // On network recovery, flush pending ops then catch up on missed changes
    window.addEventListener('online', async () => {
      console.log('Network online — flushing pending ops and catching up');
      await this.processPendingQueue();
      for (const caseId of this.subscribedCaseIds) {
        await this.performCatchUpSync(caseId);
      }
    });
  }

  /**
   * Set up handlers for incoming WebSocket events
   */
  setupEventHandlers() {
    // Clean up any existing handlers
    this.cleanup();

    // Handle incoming juror created
    this.eventUnsubscribers.push(
      WebSocketClient.on('juror:created', async (data) => {
        await this.handleJurorCreated(data);
      })
    );

    // Handle incoming juror updates
    this.eventUnsubscribers.push(
      WebSocketClient.on('juror:updated', async (data) => {
        await this.handleJurorUpdated(data);
      })
    );

    // Handle incoming juror deletion
    this.eventUnsubscribers.push(
      WebSocketClient.on('juror:deleted', async (data) => {
        await this.handleJurorDeleted(data);
      })
    );

    // Handle batch updates
    this.eventUnsubscribers.push(
      WebSocketClient.on('juror:batch', async (data) => {
        await this.handleJurorBatch(data);
      })
    );

    // On WS reconnect, flush pending ops and catch up on missed changes
    this.eventUnsubscribers.push(
      WebSocketClient.on('ws:reconnect', async () => {
        console.log('WS reconnected — flushing pending ops and catching up');
        await this.processPendingQueue();
        for (const caseId of this.subscribedCaseIds) {
          await this.performCatchUpSync(caseId);
        }
      })
    );

    // Handle case updates
    this.eventUnsubscribers.push(
      WebSocketClient.on('case:updated', async (data) => {
        await this.handleCaseUpdated(data);
      })
    );
  }

  /**
   * Handle incoming juror created event
   */
  async handleJurorCreated({ juror, caseId }) {
    if (!juror?._id) return;

    try {
      const db = getDb();
      if (!db) return;

      const jurorId = String(juror._id);

      // Check if we already have this juror by server ID
      const existing = await db.jurors.get(jurorId);
      if (existing) return;

      // If there is a pending CREATE for this case, this WS event is very likely
      // the echo of our own batch submission arriving before the HTTP response
      // has had a chance to map the local→server ID. Defer it so we don't
      // create a duplicate record that briefly shows up in the UI.
      const pendingCreate = await db.pendingOperations
        .where({ entityType: 'juror', operationType: 'create' })
        .filter(op => op.caseId === caseId)
        .first();

      if (pendingCreate) {
        this.deferredRemoteJurors.set(jurorId, { caseId, serverJuror: juror, source: 'juror:created' });
        console.log('Deferred juror:created (own create still in flight):', jurorId);
        return;
      }

      // Juror from a collaborator — add it immediately
      await JurorRepository.upsert({
        ...juror,
        id: jurorId,
        caseId,
        _synced: true,
        _serverRevision: juror.revision ?? 0
      });
      console.log('Added new juror from collaborator:', jurorId);
    } catch (error) {
      console.error('Error handling juror:created:', error);
    }
  }

  /**
   * Handle incoming juror updated event.
   * The server sends changed fields directly (no `diff` wrapper):
   *   { caseId, jurorId, _id, revision, updated, ...changedFields }
   */
  async handleJurorUpdated(data) {
    const { jurorId, caseId, ...serverFields } = data;
    if (!jurorId) return;

    try {
      await this.applyRemoteJurorRecord(
        jurorId,
        caseId,
        { _id: jurorId, ...serverFields },
        'websocket',
      );
    } catch (error) {
      console.error('Error handling juror:updated:', error);
    }
  }

  /**
   * Handle incoming juror deleted event
   */
  async handleJurorDeleted({ jurorId }) {
    if (!jurorId) return;

    try {
      const db = getDb();
      if (!db) return;

      await db.jurors.delete(jurorId);
      console.log('Deleted juror from server event:', jurorId);
    } catch (error) {
      console.error('Error handling juror:deleted:', error);
    }
  }

  /**
   * Handle batch juror updates.
   * Server sends { caseId, changes: { created, updated, deleted } }.
   */
  async handleJurorBatch({ caseId, changes }) {
    if (!caseId || !changes) return;

    const { created = [], updated = [], deleted = [] } = changes;
    if (created.length === 0 && updated.length === 0 && deleted.length === 0) return;

    try {
      const db = getDb();
      if (!db) return;

      for (const juror of updated) {
        const jurorId = String(juror._id);
        await this.applyRemoteJurorRecord(
          jurorId,
          caseId,
          juror,
          'websocket-batch',
        );
      }

      for (const jurorId of deleted) {
        await db.jurors.delete(jurorId);
      }

      // Check once whether we have any in-flight creates for this case.
      // If so, the server's "created" list may include echoes of our own
      // batch that haven't been ID-mapped yet — skip them to avoid duplicates.
      const hasPendingCreate = created.length > 0
        ? await db.pendingOperations
            .where({ entityType: 'juror', operationType: 'create' })
            .filter(op => op.caseId === caseId)
            .first()
        : null;

      for (const juror of created) {
        const jurorId = String(juror._id);
        const existing = await db.jurors.get(jurorId);
        if (existing) continue;

        if (hasPendingCreate) {
          // Own create still in-flight — defer rather than insert a duplicate.
          this.deferredRemoteJurors.set(jurorId, { caseId, serverJuror: juror, source: 'juror:batch-created' });
          console.log('Deferred batch create (own create still in flight):', jurorId);
          continue;
        }

        await JurorRepository.upsert({
          ...juror,
          id: juror._id,
          caseId,
          _synced: true,
          _serverRevision: juror.revision ?? 0
        });
      }

      console.log(`Processed batch: ${created.length} created, ${updated.length} updated, ${deleted.length} deleted`);
    } catch (error) {
      console.error('Error handling juror:batch:', error);
    }
  }

  /**
   * Handle case updated event
   */
  async handleCaseUpdated(payload = {}) {
    const { caseId, revision } = payload;
    if (!caseId) return;

    // The server broadcasts the changed fields flat on the event payload
    // ({ caseId, spacers, ..., revision }); older builds wrapped them in a
    // `diff` object. Accept both — destructuring only `diff` meant flat
    // payloads applied nothing and collaborator case updates were dropped.
    const { caseId: _cid, revision: _rev, diff, ...flatFields } = payload;
    const fields = diff && typeof diff === 'object' ? diff : flatFields;

    try {
      const db = getDb();
      if (!db) return;

      const existing = await db.cases.get(caseId);

      if (existing) {
        if (!existing._serverRevision || revision > existing._serverRevision) {
          const updated = { ...existing };
          for (const [key, value] of Object.entries(fields)) {
            updated[key] = value;
          }
          updated.revision = revision;
          updated._serverRevision = revision;
          updated._synced = true;

          await db.cases.put(updated);
          console.log('Updated case from server:', caseId, 'rev:', revision);
        }
      }
    } catch (error) {
      console.error('Error handling case:updated:', error);
    }
  }

  /**
   * Fetch a single juror from server
   */
  async fetchJuror(jurorId, caseId) {
    try {
      const response = await fetch(`${API_ENDPOINT}/api/jurors/${jurorId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const juror = await response.json();
        await JurorRepository.upsert({
          ...juror,
          id: juror._id,
          caseId,
          _synced: true,
          _serverRevision: juror.revision ?? 0
        });
      }
    } catch (error) {
      console.error('Error fetching juror:', error);
    }
  }

  /**
   * Debounced entry point for user-triggered writes (updateJuror, createJuror, etc.).
   * Waits delayMs after the last call before processing, so rapid sequences of
   * writes (e.g. filling seats) accumulate into a single batch request.
   * Direct calls to processPendingQueue() bypass the debounce (used for reconnect,
   * subscribeToCase, etc. where we want immediate sync).
   */
  schedulePendingQueueProcess(delayMs = 300) {
    // Skip scheduling while offline; the window.online handler will flush when connectivity returns.
    if (!navigator.onLine) return;

    if (this.pendingQueueDebounceTimer) {
      clearTimeout(this.pendingQueueDebounceTimer);
    }
    this.pendingQueueDebounceTimer = setTimeout(() => {
      this.pendingQueueDebounceTimer = null;
      this.processPendingQueue();
    }, delayMs);
  }

  /**
   * Process pending operations queue
   * Batches operations by case and sends to server
   */
  async processPendingQueue() {
    if (this.isProcessing) return;

    if (!navigator.onLine) return;

    if (!isDatabaseReady()) {
      console.warn('processPendingQueue skipped: database not initialized');
      return;
    }
    
    this.isProcessing = true;

    try {
      const pending = await PendingRepository.getAll();
      
      if (pending.length === 0) {
        this.isProcessing = false;
        return;
      }

      // Group by case
      // For case-type operations, caseId is null — use entityId instead
      const byCaseId = {};
      for (const op of pending) {
        const groupKey = op.entityType === 'case' ? op.entityId : op.caseId;
        if (!byCaseId[groupKey]) {
          byCaseId[groupKey] = [];
        }
        byCaseId[groupKey].push(op);
      }

      // Process each case's operations
      for (const [caseId, operations] of Object.entries(byCaseId)) {
        await this.processCaseOperations(caseId, operations);
      }

      // If we got here without throwing, mark success
      this.lastSuccessfulSync = Date.now();
      this.consecutiveSyncFailures = 0;
      this.lastSyncError = null;

    } catch (error) {
      console.error('Error processing pending queue:', error);
      this.consecutiveSyncFailures++;
      this.lastSyncError = error;
      // Retry after delay
      this.scheduleRetry();
    } finally {
      // Always reset isProcessing to prevent it from getting stuck
      this.isProcessing = false;
    }
  }

  /**
   * Process operations for a single case
   */
  async processCaseOperations(caseId, operations) {
    // Separate by entity type
    const jurorOps = operations.filter(op => op.entityType === 'juror');
    const caseOps = operations.filter(op => op.entityType === 'case');

    let activeCaseId = caseId;

    // If caseId is a local UUID but there's no pending case-create op,
    // the create op was likely lost (e.g. server crashed after create
    // succeeded but before the ID mapping completed). Auto-create now.
    if (isLocalId(activeCaseId) && !caseOps.some(op => op.operationType === 'create')) {
      const db = getDb();
      if (db) {
        // First check if the case was already mapped (created on a prior run)
        const allCases = await db.cases.toArray();
        const mapped = allCases.find(c => c._localId === activeCaseId && !isLocalId(c._id));
        if (mapped) {
          activeCaseId = mapped._id;
          console.log(`Resolved orphaned case UUID ${caseId} → ${activeCaseId}`);
        } else {
          // Case still has local UUID — create it on the server
          const localCase = await db.cases.get(activeCaseId);
          if (localCase) {
            console.log(`Auto-creating unsynced case ${activeCaseId} on server...`);
            const { _id, _localOnly, _localId, _synced, _serverRevision, _encrypted, ...serverData } = localCase;
            try {
              const response = await fetch(`${API_ENDPOINT}/api/cases`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(serverData)
              });
              if (response.ok) {
                const serverCase = await response.json();
                await this.updateLocalCaseIdToServerId(activeCaseId, serverCase._id, serverCase);
                console.log(`Auto-created case: ${activeCaseId} → ${serverCase._id}`);
                activeCaseId = serverCase._id;
              } else {
                const errBody = await response.json().catch(() => ({}));
                console.error(`Auto-create case failed: ${response.status}`, errBody);
                return; // Can't proceed without a server case
              }
            } catch (error) {
              console.error('Auto-create case error:', error);
              return;
            }
          }
        }
      }
    }

    // Process case operations FIRST (creates must happen before juror syncs)
    for (const op of caseOps) {
      const newId = await this.processCaseOperation(activeCaseId, op);
      if (newId && newId !== activeCaseId) {
        // Case was created — server assigned a new ID, update juror ops
        activeCaseId = newId;
      }
    }

    // Process juror operations as batch (using the potentially-updated caseId)
    if (jurorOps.length > 0) {
      await this.processJurorBatch(activeCaseId, jurorOps);
    }
  }

  /**
   * Process juror operations as a batch
   * Sends { created: [...], updated: [...], deleted: [...] } to match server API
   */
  async processJurorBatch(caseId, operations) {
    // If caseId is a local UUID, the case may have been created on a prior run
    // but the juror ops still reference the old ID. Resolve to server ID.
    if (isLocalId(caseId)) {
      const db = getDb();
      if (db) {
        const allCases = await db.cases.toArray();
        const mapped = allCases.find(c => c._localId === caseId);
        if (mapped && !isLocalId(mapped._id)) {
          console.log(`Resolved local case UUID ${caseId} → server ID ${mapped._id}`);
          caseId = mapped._id;
          // Also fix the juror records' caseId
          const jurors = await db.jurors.where('caseId').equals(caseId).toArray();
          if (jurors.length === 0) {
            // Jurors still reference old local caseId
            const oldJurors = await db.jurors.toArray();
            for (const j of oldJurors) {
              if (j.caseId === operations[0]?.caseId) {
                await db.jurors.put({ ...j, caseId: mapped._id });
              }
            }
          }
        } else {
          // Case hasn't been synced yet — skip juror batch for now
          console.warn(`Case ${caseId} not yet synced to server, deferring juror sync`);
          return;
        }
      }
    }

    const created = [];
    const updated = [];
    const deleted = [];
    const localCreateIds = []; // Track local IDs for created jurors (in order)
    const processedIds = [];

    // Batch fetch fresh juror data to avoid O(N) gets
    const db = getDb();
    const entityIds = operations.map(op => op.entityId);
    const freshJurors = db ? await db.jurors.where(':id').anyOf(entityIds).toArray() : [];
    const freshMap = new Map(freshJurors.map(j => [j._id, j]));

    for (const op of operations) {
      processedIds.push(op.id);

      if (op.operationType === 'create') {
        const jurorData = freshMap.get(op.entityId) || op.data;
        // Strip internal fields — server expects plain juror data
        const { _localOnly, _localId, _synced, _serverRevision, _encrypted, id, ...cleanData } = jurorData;
        localCreateIds.push(op.entityId);
        created.push(cleanData);
      } else if (op.operationType === 'update') {
        const freshJuror = freshMap.get(op.entityId);
        const changeDiff = op.diff || op.data || {};
        if (freshJuror && Object.keys(changeDiff).length > 0) {
          const { revision, ...fieldDiff } = changeDiff;
          updated.push({
            _id: freshJuror._id,
            revision: revision ?? freshJuror.revision ?? 0,
            ...fieldDiff,
          });
        }
      } else if (op.operationType === 'delete') {
        deleted.push(op.entityId);
      }
    }

    if (created.length === 0 && updated.length === 0 && deleted.length === 0) return;

    // Chunking: If batch is too large, split it (server-side limits)
    const BATCH_SIZE_LIMIT = 500;
    if (created.length > BATCH_SIZE_LIMIT || updated.length > BATCH_SIZE_LIMIT || deleted.length > BATCH_SIZE_LIMIT) {
       // For now, let's just log a warning and proceed, or we could recursively call this.
       // Actually, it's better to implement proper chunking if we want to be robust.
       console.warn(`Large juror batch (${operations.length} ops), consider implementing chunking`);
    }

    try {
      const response = await fetch(`${API_ENDPOINT}/api/cases/${caseId}/jurors/batch-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ created, updated, deleted })
      });

      if (response.ok) {
        const result = await response.json();
        const responseData = result.responseData || result;

        // Map local IDs to server IDs for successfully created jurors
        if (responseData.created && responseData.created.length > 0) {
          const idMappings = [];
          for (let i = 0; i < localCreateIds.length && i < responseData.created.length; i++) {
            idMappings.push({
              localId: localCreateIds[i],
              serverId: String(responseData.created[i]._id),
              serverData: responseData.created[i]
            });
          }
          if (idMappings.length > 0) {
            await this.bulkUpdateLocalIdsToServerIds(idMappings, caseId);
          }
        }

        // Mark operations complete before applying server jurors
        await PendingRepository.removeMany(processedIds);

        for (const juror of responseData.updated || []) {
          const jurorId = String(juror._id);
          this.deferredRemoteJurors.delete(jurorId);
          await this.applyRemoteJurorRecord(
            jurorId,
            caseId,
            juror,
            'sync-response',
          );
        }

        await this.flushDeferredRemoteJurors();
        console.log(`Synced batch of ${created.length} creates, ${updated.length} updates, ${deleted.length} deletes`);
      } else if (response.status === 409) {
        // Conflict - need to fetch latest and resolve
        console.warn('Conflict detected, fetching latest data');
        await this.performCatchUpSync(caseId);
        await PendingRepository.removeMany(processedIds);
      } else {
        throw new Error(`Batch update failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Error processing juror batch:', error);
      throw error;
    }
  }

  /**
   * Update multiple local IDs to server IDs in a single transaction
   * @param {Array<{localId, serverId, serverData}>} mappings 
   * @param {string} caseId 
   */
  async bulkUpdateLocalIdsToServerIds(mappings, caseId) {
    try {
      const db = getDb();
      if (!db) return;

      const localIds = mappings.map(m => m.localId);
      const localJurors = await db.jurors.where(':id').anyOf(localIds).toArray();
      const localMap = new Map(localJurors.map(j => [j._id, j]));
      const followUpUpdates = [];

      await db.transaction('rw', [db.jurors, db.pendingOperations], async () => {
        const jurorsToPut = [];
        const idsToDelete = [];

        for (const mapping of mappings) {
          const local = localMap.get(mapping.localId);
          if (!local) {
            continue;
          }

          const merged = mergeCreatedJurorWithLocal(
            local,
            mapping.serverData,
            mapping.serverId,
            mapping.localId,
          );
          idsToDelete.push(mapping.localId);
          jurorsToPut.push(merged);

          // Only queue a follow-up update when the merged local state has
          // content-field changes the server doesn't know about yet (e.g. the
          // user edited the juror while the create was still in flight).
          // Explicitly exclude client-only bookkeeping fields so they are never
          // sent to the server as spurious updates.
          const CLIENT_ONLY_FIELDS = [
            '_id', 'revision', 'updated', 'created', '_localOnly', '_localId',
            'caseId', '_synced', 'id', '_encrypted', '_serverRevision',
          ];
          const catchUpDiff = calculateDiff(mapping.serverData, merged, CLIENT_ONLY_FIELDS);
          if (catchUpDiff) {
            followUpUpdates.push({
              jurorId: mapping.serverId,
              diff: { ...catchUpDiff, revision: merged.revision },
              baseRevision: merged.revision,
              caseId,
            });
          }
        }

        if (idsToDelete.length > 0) {
          await db.jurors.bulkDelete(idsToDelete);
        }
        if (jurorsToPut.length > 0) {
          await db.jurors.bulkPut(jurorsToPut);
        }

        await PendingRepository.bulkUpdateEntityIds('juror', mappings);
      });

      for (const update of followUpUpdates) {
        await PendingRepository.addUpdate(
          PendingRepository.EntityType.JUROR,
          update.jurorId,
          update.diff,
          update.baseRevision,
          update.caseId,
        );
        console.log(
          'Queued catch-up sync after create for juror:',
          update.jurorId,
          Object.keys(update.diff),
        );
      }

      if (followUpUpdates.length > 0) {
        this.schedulePendingQueueProcess();
      }

      console.log(`Mapped ${mappings.length} local IDs to server IDs`);
    } catch (error) {
      console.error('Error in bulk ID mapping:', error);
    }
  }

  /**
   * Update a local ID to server ID after creation
   */
  async updateLocalIdToServerId(localId, serverId, caseId) {
    try {
      const db = getDb();
      if (!db) return;

      const local = await db.jurors.get(localId);
      if (local) {
        // Delete the record stored under the local UUID key
        await db.jurors.delete(localId);
        
        // Re-add with server ID as the primary key (_id)
        await db.jurors.put({
          ...local,
          _id: serverId,
          _synced: true,
          _localOnly: false,
          _localId: localId // Keep reference to old local ID
        });
        
        console.log(`Mapped local ID ${localId} to server ID ${serverId}`);
      }
    } catch (error) {
      console.error('Error updating local to server ID:', error);
    }
  }

  /**
   * Process a single case operation
   * Returns a new caseId if the case was created on the server (ID mapping)
   */
  async processCaseOperation(caseId, operation) {
    try {
      if (operation.operationType === 'create') {
        // Read fresh data from Dexie (pending operation data may be stale
        // due to the old encryption hooks mutating objects in-place)
        const db = getDb();
        const freshCase = db ? await db.cases.get(caseId) : null;
        const caseData = freshCase || operation.data;

        // Strip internal/local fields before sending to server
        const { _id, _localOnly, _localId, _synced, _serverRevision, _encrypted, ...serverData } = caseData;

        // POST to create the case on the server
        const response = await fetch(`${API_ENDPOINT}/api/cases`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(serverData)
        });

        if (response.ok) {
          const serverCase = await response.json();
          const serverId = serverCase._id;

          // Map local UUID → server ID in Dexie
          await this.updateLocalCaseIdToServerId(caseId, serverId, serverCase);
          await PendingRepository.remove(operation.id);
          console.log(`Case created on server: ${caseId} → ${serverId}`);
          return serverId;
        } else {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(`Case create failed: ${response.status} ${errBody.error || ''}`);
        }

      } else if (operation.operationType === 'update') {
        const response = await fetch(`${API_ENDPOINT}/api/cases/${caseId}/diff`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ diff: operation.diff || operation.data })
        });

        if (response.ok) {
          await PendingRepository.remove(operation.id);
        } else if (response.status === 409) {
          await this.performCatchUpSync(caseId);
          await PendingRepository.remove(operation.id);
        } else {
          throw new Error(`Case update failed: ${response.status}`);
        }
      } else if (operation.operationType === 'delete') {
        const response = await fetch(`${API_ENDPOINT}/api/cases/${caseId}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (response.ok || response.status === 404) {
          // 404 means already deleted on server — that's fine
          await PendingRepository.remove(operation.id);
          console.log(`Case deleted on server: ${caseId}`);
        } else {
          throw new Error(`Case delete failed: ${response.status}`);
        }
      }

      return caseId;
    } catch (error) {
      console.error('Error processing case operation:', error);
      throw error;
    }
  }

  /**
   * Replace local case UUID with server-assigned ID throughout the local DB
   */
  async updateLocalCaseIdToServerId(localId, serverId, serverCase) {
    try {
      const db = getDb();
      if (!db) return;

      // 1. Replace the case record: delete local UUID entry, add server ID entry
      const localCase = await db.cases.get(localId);
      if (localCase) {
        await db.cases.delete(localId);
        await db.cases.put({
          ...localCase,
          ...serverCase,
          _id: serverId,
          _localOnly: false,
          _localId: localId,
          _synced: true
        });
      }

      // 2. Update jurors that reference the local case ID
      const jurors = await db.jurors.where('caseId').equals(localId).toArray();
      for (const juror of jurors) {
        await db.jurors.put({ ...juror, caseId: serverId });
      }

      // 3. Update pending operations that reference the local case ID
      await PendingRepository.updateEntityId('case', localId, serverId);

      console.log(`Mapped case ID: ${localId} → ${serverId} (${jurors.length} jurors updated)`);
    } catch (error) {
      console.error('Error updating local case ID to server ID:', error);
    }
  }

  /**
   * Perform catch-up sync for a case
   * Uses the changes endpoint to get incremental updates.
   * Falls back to full sync when change data has expired (410) or on first sync.
   */
  async performCatchUpSync(caseId) {
    if (this.syncInProgress) return;
    if (!navigator.onLine) return;
    this.syncInProgress = true;

    try {
      const db = getDb();
      if (!db) return;

      // Get last sync timestamp
      const syncMeta = await db.syncMetadata.get(caseId);
      const since = syncMeta?.value || 0;

      // If we've never synced this case, skip straight to full sync
      if (since === 0) {
        console.log(`No sync metadata for case ${caseId}, performing full sync`);
        this.syncInProgress = false;
        return this.performFullSync(caseId);
      }

      const response = await fetch(
        `${API_ENDPOINT}/api/cases/${caseId}/changes?since=${since}`,
        { credentials: 'include' }
      );

      // 410 Gone → change data expired, fall back to full sync
      if (response.status === 410) {
        console.log(`Change data expired for case ${caseId}, falling back to full sync`);
        this.syncInProgress = false;
        return this.performFullSync(caseId);
      }

      // 404 → case no longer exists on server, skip silently
      if (response.status === 404) {
        console.warn(`Case ${caseId} not found on server during catch-up sync`);
        return;
      }

      if (!response.ok) {
        throw new Error(`Catch-up sync failed: ${response.status}`);
      }

      const data = await response.json();

      // Server returns { caseChanges, jurorChanges: { created, updated, deleted }, currentRevision, updatedAt }
      const { caseChanges, jurorChanges, currentRevision, updatedAt } = data;

      // Apply case-level changes
      if (caseChanges && Object.keys(caseChanges).length > 0) {
        await CaseRepository.upsert({
          ...caseChanges,
          _id: caseId,
          revision: currentRevision,
          _synced: true,
          _serverRevision: currentRevision
        });
      }

      // Apply juror-level changes
      if (jurorChanges) {
        for (const juror of jurorChanges.created || []) {
          const jurorId = String(juror._id);
          const hasPending = await db.pendingOperations
            .where({ entityType: 'juror', entityId: jurorId })
            .first();
          if (hasPending) continue; // Local unsynced changes take precedence
          await JurorRepository.upsert({
            ...juror,
            id: juror._id,
            caseId,
            _synced: true
          });
        }
        for (const juror of jurorChanges.updated || []) {
          const jurorId = String(juror._id);
          const hasPending = await db.pendingOperations
            .where({ entityType: 'juror', entityId: jurorId })
            .first();
          if (hasPending) continue; // Local unsynced changes take precedence

          // The catch-up endpoint returns only changed fields, not full juror data.
          // Merge into the existing record so we don't wipe fields like nameAndId/comments.
          const existing = await db.jurors.get(jurorId);
          if (existing) {
            await db.jurors.put({
              ...existing,
              ...juror,
              id: juror._id || existing.id,
              caseId,
              _synced: true
            });
          } else {
            await JurorRepository.upsert({
              ...juror,
              id: juror._id,
              caseId,
              _synced: true
            });
          }
        }
        for (const jurorId of jurorChanges.deleted || []) {
          await db.jurors.delete(jurorId);
        }
      }

      // Update sync metadata
      await db.syncMetadata.put({
        key: caseId,
        value: updatedAt ? new Date(updatedAt).getTime() : Date.now()
      });

      const totalChanges =
        Object.keys(caseChanges || {}).length +
        (jurorChanges?.created?.length || 0) +
        (jurorChanges?.updated?.length || 0) +
        (jurorChanges?.deleted?.length || 0);
      console.log(`Catch-up sync complete for case ${caseId}: ${totalChanges} changes`);

      // Successful server round-trip — mark sync healthy
      this.lastSuccessfulSync = Date.now();
      this.consecutiveSyncFailures = 0;
      this.lastSyncError = null;

    } catch (error) {
      console.error('Error performing catch-up sync:', error);
      this.consecutiveSyncFailures++;
      this.lastSyncError = error;
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Full sync for a case - fetches all jurors
   */
  async performFullSync(caseId) {
    try {
      const response = await fetch(`${API_ENDPOINT}/api/cases/${caseId}/jurors`, {
        credentials: 'include'
      });

      // 404 / 403 → case doesn't exist or user lost access — skip silently
      if (response.status === 404 || response.status === 403) {
        console.warn(`Full sync skipped for case ${caseId}: ${response.status}`);
        return;
      }

      if (!response.ok) {
        throw new Error(`Full sync failed: ${response.status}`);
      }

      const jurors = await response.json();
      const db = getDb();
      if (!db) return;

      // Build set of server juror IDs for orphan cleanup
      const serverIds = new Set(jurors.map(j => String(j._id)));

      // Upsert all jurors from server, skipping any with pending local changes
      for (const juror of jurors) {
        const jurorId = String(juror._id);
        const hasPending = await db.pendingOperations
          .where({ entityType: 'juror', entityId: jurorId })
          .first();
        if (hasPending) continue;
        await JurorRepository.upsert({
          ...juror,
          id: juror._id,
          caseId,
          _synced: true,
          _serverRevision: juror.revision ?? 0
        });
      }

      // Clean up orphaned local-only jurors for this case.
      // Only safe to do this when we're confident server data is authoritative,
      // i.e. we've had a recent successful sync. If sync has been failing,
      // local-only jurors might just be unsynced — deleting them causes data loss.
      const recentSyncThreshold = 5 * 60 * 1000; // 5 minutes
      const hasRecentSync = this.lastSuccessfulSync && (Date.now() - this.lastSuccessfulSync) < recentSyncThreshold;

      if (hasRecentSync) {
        const localJurors = await db.jurors.where('caseId').equals(caseId).toArray();
        for (const local of localJurors) {
          if (local._localOnly && !serverIds.has(local._id)) {
            // Check if there's no pending operation for this juror
            const hasPending = await db.pendingOperations
              .where({ entityType: 'juror', entityId: local._id })
              .first();
            if (!hasPending) {
              await db.jurors.delete(local._id);
              console.log(`Cleaned up orphaned local juror: ${local._id}`);
            }
          }
        }
      } else {
        console.log(`Skipping orphan cleanup for case ${caseId}: no recent successful sync`);
      }

      // Update sync metadata
      await db.syncMetadata.put({
        key: caseId,
        value: Date.now()
      });

      console.log(`Full sync complete for case ${caseId}: ${jurors.length} jurors`);

      // Successful server round-trip — mark sync healthy
      this.lastSuccessfulSync = Date.now();
      this.consecutiveSyncFailures = 0;
      this.lastSyncError = null;

    } catch (error) {
      console.error('Error performing full sync:', error);
      this.consecutiveSyncFailures++;
      this.lastSyncError = error;
      throw error;
    }
  }

  /**
   * Fetch all cases from the server and store in Dexie
   * Used to populate the case list on initial load
   */
  async fetchAllCases() {
    if (!isDatabaseReady()) {
      console.warn('fetchAllCases skipped: database not initialized');
      return [];
    }

    try {
      // Always fetch archived cases too — the server excludes them by
      // default, but the offline-first UI filters locally so the
      // "include archived" toggle works without a network round-trip.
      const response = await fetch(`${API_ENDPOINT}/api/cases?includeArchived=true`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Fetch cases failed: ${response.status}`);
      }

      const data = await response.json();
      
      // Server returns { cases: [...], groupIds: [...] }
      const cases = data.cases || data;
      
      if (Array.isArray(cases) && cases.length > 0) {
        await CaseRepository.bulkSaveFromServer(cases);
        console.log(`Fetched ${cases.length} cases from server`);

        // Background-prefetch jurors for the 10 most recently updated cases so
        // they are available for offline use without manual navigation.
        const recent = [...cases]
          .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
          .slice(0, 10);
        for (const c of recent) {
          this.performFullSync(String(c._id)).catch(err =>
            console.warn(`Background prefetch failed for case ${c._id}:`, err.message)
          );
        }
      }

      // Successful server round-trip — mark sync healthy
      this.lastSuccessfulSync = Date.now();
      this.consecutiveSyncFailures = 0;
      this.lastSyncError = null;

      return cases;
    } catch (error) {
      console.error('Error fetching all cases:', error);
      this.consecutiveSyncFailures++;
      this.lastSyncError = error;
      throw error;
    }
  }

  /**
   * Schedule a retry for failed operations
   */
  scheduleRetry(delayMs = 5000) {
    // No point scheduling a retry while offline; the window.online handler will re-trigger.
    if (!navigator.onLine) return;

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    this.retryTimeout = setTimeout(() => {
      this.processPendingQueue();
    }, delayMs);
  }

  /**
   * Subscribe to a case and sync data.
   * Non-fatal: if the subscription or sync fails (e.g. case was deleted on server,
   * or user lost access), the local data is still usable.
   */
  async subscribeToCase(caseId) {
    this.subscribedCaseIds.add(caseId);

    // Subscribe to WebSocket updates (server may reject asynchronously — that's OK)
    try {
      await WebSocketClient.subscribeToCase(caseId);
    } catch (err) {
      console.warn(`WebSocket subscribe failed for case ${caseId}:`, err.message);
    }

    // Push pending local changes to the server BEFORE pulling server data,
    // so that catch-up sync sees the latest server state (including our changes)
    // rather than overwriting local pending data with stale server data.
    try {
      await this.processPendingQueue();
    } catch (err) {
      console.warn('Error processing pending queue:', err.message);
    }

    // Perform catch-up sync (falls back to full sync on first load)
    try {
      await this.performCatchUpSync(caseId);
    } catch (err) {
      console.warn(`Catch-up sync failed for case ${caseId}:`, err.message);
    }

    // Check for orphaned local-only jurors (from old bug where _localOnly
    // was never cleared after server sync). If found, do a full sync to fix.
    try {
      await this.cleanupOrphanedJurors(caseId);
    } catch (err) {
      console.warn(`Orphan cleanup failed for case ${caseId}:`, err.message);
    }
  }

  /**
   * Detect orphaned local-only jurors and trigger full sync to fix them.
   * An orphan is a juror with _localOnly: true and a UUID _id, but no
   * pending create operation — meaning it was synced but the old
   * updateLocalIdToServerId bug left it with stale flags.
   *
   * Only runs cleanup when we've had a recent successful sync, to avoid
   * deleting jurors that are simply unsynced due to connectivity/auth issues.
   */
  async cleanupOrphanedJurors(caseId) {
    const db = getDb();
    if (!db) return;

    // Don't run orphan cleanup if sync hasn't been working recently
    const recentSyncThreshold = 5 * 60 * 1000; // 5 minutes
    const hasRecentSync = this.lastSuccessfulSync && (Date.now() - this.lastSuccessfulSync) < recentSyncThreshold;
    if (!hasRecentSync) {
      console.log(`Skipping orphan detection for case ${caseId}: no recent successful sync`);
      return;
    }

    const localJurors = await db.jurors.where('caseId').equals(caseId).toArray();
    let hasOrphans = false;

    for (const j of localJurors) {
      if (j._localOnly && isLocalId(j._id)) {
        const hasPending = await db.pendingOperations
          .where({ entityType: 'juror', entityId: j._id })
          .first();
        if (!hasPending) {
          hasOrphans = true;
          break;
        }
      }
    }

    if (hasOrphans) {
      console.log(`Found orphaned local-only jurors for case ${caseId}, performing full sync to fix...`);
      await this.performFullSync(caseId);
    }
  }

  /**
   * Unsubscribe from a case
   */
  unsubscribeFromCase(caseId) {
    this.subscribedCaseIds.delete(caseId);
    WebSocketClient.unsubscribeFromCase(caseId);
  }

  /**
   * Clean up event handlers and connections
   */
  cleanup() {
    for (const unsub of this.eventUnsubscribers) {
      unsub();
    }
    this.eventUnsubscribers = [];

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    if (this.pendingQueueDebounceTimer) {
      clearTimeout(this.pendingQueueDebounceTimer);
      this.pendingQueueDebounceTimer = null;
    }
  }

  /**
   * Get sync health status for UI display
   * @returns {{ healthy: boolean, pendingFailures: number, lastError: Error|null, lastSuccessfulSync: number|null }}
   */
  getSyncStatus() {
    return {
      healthy: this.consecutiveSyncFailures === 0,
      pendingFailures: this.consecutiveSyncFailures,
      lastError: this.lastSyncError,
      lastSuccessfulSync: this.lastSuccessfulSync
    };
  }

  /**
   * Shutdown the sync engine
   */
  shutdown() {
    this.cleanup();
    WebSocketClient.disconnect();
    this.initialized = false;
    this.isProcessing = false;
    this.syncInProgress = false;
    console.log('SyncEngine shutdown');
  }
}

// Singleton instance
const syncEngine = new SyncEngine();

export default syncEngine;

// Named exports for convenience
export const {
  initialize,
  processPendingQueue,
  schedulePendingQueueProcess,
  performCatchUpSync,
  performFullSync,
  fetchAllCases,
  subscribeToCase,
  unsubscribeFromCase,
  getSyncStatus,
  shutdown
} = {
  initialize: () => syncEngine.initialize(),
  processPendingQueue: () => syncEngine.processPendingQueue(),
  schedulePendingQueueProcess: (delayMs) => syncEngine.schedulePendingQueueProcess(delayMs),
  performCatchUpSync: (caseId) => syncEngine.performCatchUpSync(caseId),
  performFullSync: (caseId) => syncEngine.performFullSync(caseId),
  fetchAllCases: () => syncEngine.fetchAllCases(),
  subscribeToCase: (caseId) => syncEngine.subscribeToCase(caseId),
  unsubscribeFromCase: (caseId) => syncEngine.unsubscribeFromCase(caseId),
  getSyncStatus: () => syncEngine.getSyncStatus(),
  shutdown: () => syncEngine.shutdown()
};
